/**
 * Shared query-response helper utilities for feature client modules.
 *
 * These functions normalise the loosely-typed responses returned by InsForge PostgREST
 * RPC calls and PostgREST queries into the shapes expected by the domain
 * layer.  They were previously duplicated across 12+ feature client files.
 */

import { todayDateOnly } from "@/lib/format";

/** Unsafely cast an unknown value to the target type. */
export function cast<T>(value: unknown): T {
  return value as T;
}

/** Type-guard: `true` when `value` is a non-null, non-array plain object. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Extract the first plain-object record from a value that may be an array
 * or a single object.  Returns `null` when no record can be found.
 */
export function firstRecord(value: unknown): Record<string, unknown> | null {
  const normalized = Array.isArray(value) ? value[0] : value;
  return isRecord(normalized) ? normalized : null;
}

/**
 * Coerce an unknown value to a finite number, returning `null` when the
 * value cannot be meaningfully converted.
 */
export function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    // Reject partial-string coercion: parseFloat("100abc") is 100, which
    // overstates amounts when malformed input is present. Accept only when
    // the ENTIRE string is a valid number.
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    // Number(trimmed) parses the whole string; parseFloat stops at the first
    // invalid character. They must agree.
    return Number(trimmed) === parsed ? parsed : null;
  }
  return null;
}

/**
 * Throw the first PostgREST error found across a set of parallel query
 * results, in the order given. No-ops when every result is error-free.
 */
export function throwOnFirstError(results: Array<{ error: { message: string } | null }>): void {
  for (const result of results) {
    if (result.error) {
      throw new Error(result.error.message);
    }
  }
}

/**
 * Read a string property from a record, returning `null` when the property
 * is missing or not a string.
 */
export function getOptionalString(
  value: Record<string, unknown> | null,
  key: string,
): string | null {
  const candidate = value?.[key];
  return typeof candidate === "string" ? candidate : null;
}

/**
 * Normalise a date-like input to a date-only `YYYY-MM-DD` string.
 * Returns the operator's local calendar day when the input is empty or missing.
 */
export function normalizeDateOnlyInput(date?: string | null): string {
  const trimmed = date?.trim() ?? "";
  if (trimmed.length === 0) {
    return todayDateOnly();
  }
  return trimmed.split("T")[0] ?? "";
}

/** The leading `YYYY-MM-DD` of a range bound, however the rest is spelled. */
const BOUND_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * The calendar day an inclusive range bound names, as a UTC-midnight `Date`: a
 * `YYYY-MM-DD` day, or a timestamp whose leading day names it. Throws on
 * anything else.
 *
 * Built with `setUTCFullYear`, not `new Date(year, month, day)`: the
 * constructor maps years 0-99 to 1900-1999, so `0002-08-31` would read as a
 * different, "invalid" day. A date input passes through exactly those values on
 * the way to a typed 2026 (`0002`, `0020`, `0202`), and PostgREST accepts them,
 * so they are valid bounds here too. Year `0000` is not (Postgres has none).
 */
function parseBoundDay(bound: string): Date {
  const match = BOUND_DAY_RE.exec(bound.trim());
  if (match) {
    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const dayOfMonth = Number(match[3]);
    const day = new Date(0);
    day.setUTCFullYear(year, monthIndex, dayOfMonth);
    // A day that does not exist (`2026-02-30`) rolls over instead of failing, so
    // the fields are read back and compared.
    if (
      year >= 1 &&
      day.getUTCFullYear() === year &&
      day.getUTCMonth() === monthIndex &&
      day.getUTCDate() === dayOfMonth
    ) {
      return day;
    }
  }
  // Filters are rehydrated from localStorage without validation. Naming the
  // offending bound here beats both the opaque PostgREST 400 an unparseable
  // operand used to produce and the silent rollover a bare
  // `new Date("2026-02-30T00:00:00.000Z")` performs, which would have queried
  // March.
  throw new Error(`Date filter bound is not a calendar day: "${bound}"`);
}

/** The `YYYY-MM-DD` key of a UTC-midnight `Date`, the year padded to four digits. */
function utcDayKey(day: Date): string {
  const year = String(day.getUTCFullYear()).padStart(4, "0");
  const month = String(day.getUTCMonth() + 1).padStart(2, "0");
  const dayOfMonth = String(day.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${dayOfMonth}`;
}

/**
 * Resolve one inclusive day bound to the encoded day it names (the form
 * `toEncodedDay` writes), shifted by `dayOffset` whole days. Stepping a calendar
 * day keeps a month, year, or leap boundary correct without any month-length
 * arithmetic here.
 */
function toDayStartInstant(bound: string, dayOffset: number): string {
  const day = parseBoundDay(bound);
  day.setUTCDate(day.getUTCDate() + dayOffset);
  return `${utcDayKey(day)}T00:00:00.000Z`;
}

/**
 * The exclusive upper bound of a range whose last day is `endDay` (a
 * `YYYY-MM-DD` day, or a timestamp whose leading day names it): the next day's
 * start, applied with `.lt`.
 *
 * Every list filter takes its closing day from a date picker, so the bound
 * arrives as a bare day while the column is a timestamp. `.lte("date",
 * "2026-08-31")` compares against that day's midnight and drops anything the
 * column holds later on it — an invoice dated Aug 31 fell out of the August
 * range that way. `.lt(column, toPostgrestDayUntil(endDay))` covers the whole
 * closing day without reaching into the next one.
 *
 * The `*_list_totals` RPCs close the range at the same instant, from the day
 * {@link toRangeEndDay} names, which is what keeps a totals footer describing
 * the rows above it.
 *
 * Only for a `timestamptz` column. A `date` column has no time of day to
 * outrun, so an inclusive `.lte` is already right there. And only for a range:
 * a lookup that must hit the one row the server attaches to by exact `date`
 * (the same-day merge previews) is an equality, not a day window.
 */
export function toPostgrestDayUntil(endDay: string): string {
  return toDayStartInstant(endDay, 1);
}

/**
 * The `YYYY-MM-DD` day a range's end bound names, for the `p_date_to` of the
 * `*_list_totals` RPCs. (Not for the invoice bulk resolver, whose end-day rule
 * is deliberately unchanged.)
 *
 * Reads the day exactly as {@link toPostgrestDayUntil} does — the leading
 * `YYYY-MM-DD` of the bound, however the rest is spelled — so the list rows and
 * the totals footer always close on the same day. A different reading (`split("T")[0]` keeps the whole of `2026-08-31
 * 23:00:00-07:00`) hands the RPC an instant on another day.
 */
export function toRangeEndDay(endBound: string): string {
  return utcDayKey(parseBoundDay(endBound));
}

/**
 * The half-open window that an inclusive `YYYY-MM-DD` day range selects on an
 * encoded-day column (see `parseEncodedDay` in `format.ts`).
 *
 * `gl_purchase_orders.date` is `timestamp with time zone`, but nothing writes a
 * wall-clock instant into it: `xan_create_purchase_order_with_payload` takes a
 * `p_date date` and `xan_create_or_attach_document` inserts that day, which
 * lands at midnight on the database's own calendar. Every stored value is
 * therefore a day, not a moment in one — the same convention `toEncodedDay`
 * writes task and project due dates in.
 *
 * Two things follow. An inclusive `.lte(end)` compared the closing day against
 * its own midnight, so it admitted only a row stamped at that exact instant and
 * dropped anything the column carried past it; `until` is the *next* day's
 * midnight, applied with `.lt`, which covers the closing day without reaching
 * into the day after ({@link toPostgrestDayUntil}). And both bounds are encoded
 * the way the column was written rather than re-anchored at the operator's
 * local midnight: shifting them by the operator's UTC offset would move the
 * window off the stored days entirely, filing a purchase order dated the 5th
 * under a filter naming the 4th for everyone west of Greenwich.
 */
export function toPostgrestDayWindow(range: { start: string; end: string }): {
  from: string;
  until: string;
} {
  return {
    from: toDayStartInstant(range.start, 0),
    until: toPostgrestDayUntil(range.end),
  };
}

/**
 * Build a complete PostgREST `ilike` *contains* pattern that matches `value`
 * literally — double quotes and `%…%` wildcards included:
 *
 *   query.or(`name.ilike.${toPostgrestIlikeContains(term)}`)
 *
 * PostgREST's filter DSL uses `,` to separate predicates and `(`/`)` for
 * grouping, so unescaped user input inside a raw `.or(...)` string can
 * terminate the predicate and inject arbitrary filter clauses. PostgREST's
 * own grammar solves this with double-quoted values: inside `"…"` every DSL
 * delimiter is literal and only `\` and `"` are special (escaped as `\\` and
 * `\"`). Unlike the previous strip-to-space approach, legitimate punctuation
 * (`Acme, Inc`, `Vendor (US)`, `Quote:"`, `Path\Name`) therefore keeps
 * matching instead of silently returning no rows.
 *
 * Escaping order matters:
 *   1. SQL `LIKE` metacharacters (`\`, `%`, `_`) are backslash-escaped so the
 *      user's input matches literally;
 *   2. the pattern is wrapped in `%…%` — the only intentional wildcards;
 *   3. the whole pattern is escaped for PostgREST's quoted-value grammar and
 *      wrapped in double quotes.
 *
 * One transform then happens downstream of this function and cannot be escaped
 * away: PostgREST translates every `*` in a like/ilike operand into `%`, inside
 * double-quoted values as well, so a term carrying `*` always searches as a
 * wildcard however it is escaped here. Anything that has to select the same set
 * as this predicate — the `*_list_totals` RPCs behind the totals strips —
 * reproduces that translation as well; see `public.list_search_pattern` in
 * `migrations/20260726160000_add-list-search-and-totals.sql`. Widening one side
 * only is what makes a footer contradict the rows above it.
 */
export function toPostgrestIlikeContains(value: string): string {
  const likePattern = value.replaceAll("\\", "\\\\").replaceAll("%", "\\%").replaceAll("_", "\\_");
  const quoted = `%${likePattern}%`.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
  return `"${quoted}"`;
}
